import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AuthService} from '../../../services/auth.service';
import {ActivatedRoute} from '@angular/router';
import {
    fadeInLeftOnEnterAnimation,
    fadeInOnEnterAnimation,
    fadeInUpBigOnEnterAnimation,
    fadeOutOnLeaveAnimation,
    fadeOutRightOnLeaveAnimation
} from 'angular-animations';
import {UserService} from '../../../services/user.service';
import {Subscription} from 'rxjs';
import {EventsService} from '../../../services/events.service';
import {environment} from '../../../../environments/environment';
import {AnalyticsService} from '../../../services/analytics.service';
import {ReviewReportAnswerFormModel, ReviewReportFeedbackFormModel, ReviewReportFormModel} from '../models/review.model';
import {ReviewService} from '../services/review.service';
import {NavService} from '../../../services/nav.service';
import {AlertController, LoadingController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {RequesterResponseModel} from '../../../services/models/requester.model';
import {FormGroup, NgForm} from '@angular/forms';

@Component({
    selector: 'app-review-feedback-page',
    templateUrl: 'review-feedback.page.html',
    styleUrls: ['review-feedback.page.scss'],
    animations: [
        fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
        fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
        fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
        fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
        fadeInUpBigOnEnterAnimation({anchor: 'enterUp', duration: 300}),
    ],
})
export class ReviewFeedbackPage implements OnInit, OnDestroy {

    @ViewChild('form', {static: false}) form: NgForm;

    subscriptions: Subscription[] = [];

    environment = null;
    menuVisible = true;

    report_id: number = null;
    feedback: ReviewReportFeedbackFormModel = null;
    feedbackPromise: Promise<ReviewReportFeedbackFormModel> = null;
    feedbackValid = false;

    userType = null;
    level = 'learner';

    platform = 'website';

    isSaved = true;
    isSaving = false;

    showNotifications  = false;

    constructor(
        private event: EventsService,
        public authService: AuthService,
        public userService: UserService,
        private reviewService: ReviewService,
        private analytics: AnalyticsService,
        private activatedRoute: ActivatedRoute,
        private nav: NavService,
        private alertController: AlertController,
        private translate: TranslateService,
        private loadingCtrl: LoadingController
    ) {
        this.environment = environment;
        this.subscriptions.push(
            this.event.subscribe('login', () => {
                this.feedbackValid = false;
            })
        );
        this.subscriptions.push(
            this.event.subscribe('logout', () => this.feedbackValid = false)
        );

        this.subscriptions.push(
            this.event.subscribe('form:changed', (formName: string) => {
                if (formName === 'review-feedback') {
                    this.isSaved = false;
                }
            })
        );
    }

    ngOnInit(): void {
        this.platform = environment.platform;
        this.report_id = Number(this.activatedRoute.snapshot.paramMap.get('report_id'));
        this.level = this.activatedRoute.snapshot.data.level;
    }


    ionViewWillEnter() {
        this.menuVisible = false;
        this.userType = this.authService.getUserType();

        if (this.feedback === null || !this.feedbackValid) {
            this.feedbackPromise = this.reviewService.getReviewReportFeedback(this.report_id);
        }
    }

    ionViewDidEnter() {
        this.analytics.trackView('My Professional Development Reviews');

        if (this.feedback === null || !this.feedbackValid) {
            this.feedbackPromise.then(data => {
                if (data === null) {
                    this.alertController.create({
                        header: this.translate.instant('__ALERT_ERROR_TITLE__'),
                        message: this.translate.instant('__ALERT_ERROR_EMPTY_DATA_MESSAGE__'),
                        buttons: [
                            {
                                text: this.translate.instant('__ALERT_ERROR_BUTTON__'),
                                handler: () => {
                                    this.goBack();
                                }
                            }]
                    }).then(a => a.present());
                } else {
                    this.feedback = data;
                    this.feedbackValid = true;
                }
            });
        }
    }

    onFormChange(event) {
        this.event.publish('form:changed', 'review-feedback');
    }

    onSubmit() {
        Object.keys(this.form.controls).forEach(key => {
            if (this.form.controls[key] instanceof FormGroup) {
                Object.keys(this.form.controls[key]['controls']).forEach(key2 => {
                    this.form.controls[key]['controls'][key2].markAsTouched();
                })
            } else {
                this.form.controls[key].markAsTouched();
            }
        });

        if (!this.form.valid) {
            this.displayErrorAlert('__ALERT_ERROR_MESSAGE_FILL_REQUIRED_FIELDS__');
            return;
        }

        this.isSaving = true;
        this.reviewService.saveReviewReportFeedback(this.feedback).then(data => {
            this.isSaving = false;
            if (!!(data as RequesterResponseModel).status && (data as RequesterResponseModel).status === 'ok') {
                this.isSaved = true;
                this.event.publish('form:saved', {name: 'review-feedback'});
                if (this.feedback.answers.value.filter(i => !i.answer.value).length) {
                    this.displaySuccessAlert((data as RequesterResponseModel).message);
                } else {
                    this.displayCompleteAlert();
                }
            } else {
                this.feedback = data as ReviewReportFeedbackFormModel;
                this.displayErrorAlert('__ALERT_ERROR_MESSAGE_FILL_REQUIRED_FIELDS__');
            }
        }).catch(e => {
            this.isSaving = false;
        })
    }

    onStatusChange(status: boolean) {
        if (!this.isSaved || !this.report_id) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__REVIEW_ALERT_UNSAVED_MESSAGE__'),
                buttons: [this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.changeStatus(!status);
        }
    }

    changeStatus(status: boolean) {
        this.showLoading();
        return this.reviewService.updateMyReviewReportFeedbackStatus(this.feedback.id, status).then(data => {
            if (!!data.status && data.status === 'ok') {
                this.feedbackValid = false;
                this.event.publish('tasks:updateTaskStatus');
                return this.reviewService.getReviewReportFeedback(this.report_id)
                    .then(data => {
                        this.feedback = data;
                        this.feedbackValid = true;
                    });
            }
        });
    }

    displayCompleteAlert() {
        this.alertController.create({
            header: this.translate.instant('__FEEDBACK_COMPLETE_ALERT_TITLE__'),
            message: this.translate.instant('__FEEDBACK_COMPLETE_ALERT_MESSAGE__'),
            buttons: [{
                text: this.translate.instant('__ALERT_YES_BUTTON__'),
                handler: () => {
                    this.changeStatus(true).then(() => {
                        this.goBack();
                    })
                }
            }, {
                text: this.translate.instant('__ALERT_NO_BUTTON__'),
                cssClass: 'alert-controller-warning',
                handler: () => {
                    this.goBack();
                }
            }]
        }).then(a => a.present());
    }

    displayErrorAlert(message: string) {
        this.alertController.create({
            header: this.translate.instant('__ALERT_ERROR_TITLE__'),
            message: this.translate.instant(message),
            buttons: [this.translate.instant('__ALERT_ERROR_BUTTON__')]
        }).then(a => a.present());
    }

    displaySuccessAlert(message: string) {
        this.alertController.create({
            header: this.translate.instant('__ALERT_SUCCESS_TITLE__'),
            message: this.translate.instant(message),
            buttons: [this.translate.instant('__ALERT_SUCCESS_BUTTON__')]
        }).then(a => a.present());
    }

    async showLoading() {
        const loading = await this.loadingCtrl.create({
            message: 'Loading...',
            duration: 1000,
            spinner: 'circles',
        });

        loading.present();
    }

    goBack() {
        this.nav.navigate(['/tasks']);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(i => i.unsubscribe());
    }
}
